import { ref } from 'vue'
import { message } from 'ant-design-vue'
import debounce from 'awesome-debounce-promise'

export interface UseActionOptions<_ extends unknown[], TResult> {
  showMessage: boolean
  successMessage?: string
  errorMessage?: string
  onSuccess?: (result: TResult) => void
  onError?: (error: unknown) => void
  debounceTime?: number // 防抖时间，单位：毫秒
  immediateFirstCall?: boolean // 新增：是否首呼立即执行（默认 true）
}

export function useMutationData<TArgs extends unknown[], TResult>(
  action: (...args: TArgs) => Promise<TResult>,
  options: UseActionOptions<TArgs, TResult> = {
    showMessage: false,
    successMessage: '操作成功',
    errorMessage: '操作失败',
    debounceTime: 1000,
    immediateFirstCall: true,
  },
) {
  const loading = ref(false)

  const mutationData = async (...args: TArgs): Promise<TResult | undefined> => {
    loading.value = true
    try {
      const result = await action(...args)
      if (options.showMessage) message.success(options.successMessage)
      options.onSuccess?.(result)
      // 返回值优化：仅在 TResult 有意义时返回，否则返回 undefined
      if (result !== undefined && result !== null) {
        return result as TResult
      }
      return undefined // 显式返回 undefined
    } catch (error: unknown) {
      options.onError?.(error)
      // 注意：错误已在 parsers.ts 中统一处理，showMessage 的错误提示已废弃，保留参数是为了向后兼容
      // if (options.showMessage) {
      //   message.error(error instanceof Error ? error.message : options.errorMessage)
      // }
      console.error('操作失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const execute = options.debounceTime
    ? debounce(mutationData, options.debounceTime, {
        leading: options.immediateFirstCall !== false,
      })
    : mutationData

  return {
    loading,
    execute,
  }
}
